import { useEffect, useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * Copies a readable value to the clipboard.
 *
 * Offered for variables only, and that asymmetry is the point: a variable's
 * value is readable by definition, so copying it grants nothing the API has not
 * already granted — and copying while it stays masked is better than revealing
 * it first. A secret has nothing to copy — the service returns no value — and a
 * control here would imply otherwise.
 *
 * Confirms in place rather than through a toast: the feedback belongs at the
 * thing that was clicked, and a toast for something this small is noise.
 *
 * Always drawn, not revealed on hover. It shares a cell with the reveal toggle
 * now, and appearing on hover changed the width of that cluster — which shifted
 * the toggle sideways at the moment the pointer was travelling towards it.
 */
export function CopyValueButton({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => {
      setCopied(false);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);

  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-7 shrink-0"
      onClick={() => {
        // Absent in insecure contexts and in some test environments; copying is
        // a convenience, so its absence must not throw at the user.
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
        });
      }}
    >
      {copied ? (
        <CheckIcon aria-hidden="true" />
      ) : (
        <CopyIcon aria-hidden="true" />
      )}
      <span className="sr-only">
        {copied ? `Copied ${label}` : `Copy the value of ${label}`}
      </span>
    </Button>
  );
}
